import { WidgetType } from '@features/dashboard/enums';
import { WidgetPayload } from '@features/dashboard/types';
import { makeLadderStore } from '@features/dashboard/widgets/ladder/context/LadderStore';
import { OrderTrackerWidgetPayload } from '@features/dashboard/widgets/order-tracker/OrderTrackerWidget';
import { makeOrderTrackerStore } from '@features/dashboard/widgets/order-tracker/store/OrderTrackerStore';
import { create, StateCreator, StoreApi, UseBoundStore } from 'zustand';
import { LadderPayload } from '../../dashboard/widgets/ladder/LadderTypes';
import { AppStore } from './AppStore';
import { WidgetSubStore } from './StoreTypes';

// The aim of the widget store is to allow separate store types to be used on a per-widget basis.
// Right now that means providing a separate Zustand store for each widget, which should allow
// development to be a lot more incremental, but it does mean having multiple Zustand instances
// hanging around.

// The alternative is a single store for each widget type, but that's going to mean passing the widget id around
// a lot, and / or currying functions to ensure the id is present to update the right bit of state.

// If anyone has any bright ideas about a better solution, feel free to refactor things.

interface WidgetStoreType {
  id: string;

  type: WidgetType;

  // Hook to use in the widget - see AppStore::registerWidget below
  storeHook: UseBoundStore<StoreApi<WidgetSubStore>>;

  payload?: WidgetPayload;
}

interface EmptyWidgetStore extends WidgetSubStore {
  message: string;
  initFromPayload: (props: WidgetPayload | undefined) => boolean;
}

const emptyStoreCreator: StateCreator<EmptyWidgetStore> = set => {
  return {
    message: 'This widget type does not have any store associated with it',
    initFromPayload: () => false,
  };
};

type RegisterWidgetFn = (type: WidgetType, id: string, payload: WidgetPayload) => WidgetStoreType;

type WidgetMap = {
  [widgetId: string]: WidgetStoreType;
};

export interface WidgetStore {
  widgets: WidgetMap;

  registerWidget: RegisterWidgetFn;

  unregisterWidget: (id: string) => void;

  isWidgetRegistered: (id: string) => boolean;

  getWidgetData: (id: string) => WidgetStoreType | null;

  getOrCreateWidgetData: RegisterWidgetFn;
}

export const makeWidgetStore: StateCreator<AppStore, [], [], WidgetStore> = (set, get) => {
  return {
    widgets: {},

    registerWidget: (type, id, payload): WidgetStoreType => {
      const existing = get().widgets[id];

      if (existing) {
        return existing;
      }

      const newWidgets = { ...get().widgets };
      let typedPayload: WidgetPayload;
      switch (type) {
        case WidgetType.Ladder:
          typedPayload = payload as LadderPayload;
          newWidgets[id] = { id, type, storeHook: create(makeLadderStore), payload: typedPayload };
          break;

        case WidgetType.OrderTracker:
          typedPayload = payload as OrderTrackerWidgetPayload;
          newWidgets[id] = { id, type, storeHook: create(makeOrderTrackerStore), payload: payload as OrderTrackerWidgetPayload };
          break;

        default:
          newWidgets[id] = { id, type, storeHook: create(emptyStoreCreator), payload };
          break;
      }

      set({ widgets: newWidgets });

      return get().widgets[id] || null;
    },

    unregisterWidget: (id: string) => {
      const { widgets } = get();
      if (widgets[id]) {
        delete widgets[id];
      }
      set({ widgets });
    },

    isWidgetRegistered: (id: string) => {
      return get().widgets[id] !== undefined;
    },

    getOrCreateWidgetData: (type, id, payload) => {
      const existing = get().getWidgetData(id);
      if (existing) {
        return existing;
      }
      return get().registerWidget(type, id, payload);
    },

    getWidgetData: (id: string) => {
      return get().widgets[id] || null;
    },

    common: null,
  };
};
